class KitColorPickerBuilder extends HTMLElement {
    constructor() {
      super();
      this.productHandle = this.getAttribute('data-product-handle');
      this.selectableColors = parseInt(this.getAttribute('data-number-color')) || 6;
      this.freeColors = parseInt(this.getAttribute('data-free-colors')) || 0;
      this.selectedColors = new Map();
      this.extraColors = new Map(); 
      this.currentColorIndex = null;
      this.allowAdditionalColors = this.querySelector('.kit-color-picker-builder-container')?.dataset.allowAdditionalColors === 'true';
      this.extraColorCount = 0; 
      this.extraColorsDiscount = this.getAttribute('data-extra-colors-discount');
  
      // Get kit builder type (popup_style or default)
      this.kitBuilderType = this.getAttribute('data-kit-builder-type') || 'default';
      this.isPopupStyle = this.kitBuilderType === 'popup_style';
  
      // Get kit builder configuration
      this.config = {
        kitBuilderSelectedKitVariantId: this.getAttribute('data-kit-builder-selected-kit-variant-id'),
        kitBuilderBundleDiscount: this.getAttribute('data-kit-builder-bundle-discount'),
        kitGiftVariantIds: this.getAttribute('data-kit-gift-variant-ids')
      };
  
      // Upsell related properties
      this.upsellPopup = null;
      this.upsellOverlay = null; 
      this.isUpsellEnabled = false;
      this.upsellLimit = 1;
      this.freeShippingThreshold = 4900; 
      this.currentKitPrice = 0; 
      this.selectedUpsellItems = new Map(); 
  
      // Get elements references
      this.upsellCloseButton = null;
      this.upsellOptionInputs = [];
      this.upsellAddButton = null;
      this.upsellDeclineButton = null;
      this.upsellProgressText = null;
      this.upsellProgressBarInner = null;
  
      setTimeout(() => this.initialize(), 0);
    }
  
    initialize() {
      this.drawer = this.querySelector('.kit-color-picker-drawer');
      if (this.drawer) {
        this.setupEventListeners();
      } else {
        console.warn('Color picker drawer not found');
      }
  
      // Initialize filter states
      this.currentCategoryFilter = 'all';
      this.currentColorFilter = null;
  
      // Apply popup style class if needed
      if (this.isPopupStyle) {
        this.classList.add('kit-color-picker-builder--popup-style');
      }
  
      // Make sure we have the latest config values
      this.config = {
        kitBuilderSelectedKitVariantId: this.getAttribute('data-kit-builder-selected-kit-variant-id'),
        kitBuilderBundleDiscount: this.getAttribute('data-kit-builder-bundle-discount'),
        kitGiftVariantIds: this.getAttribute('data-kit-gift-variant-ids')
      };
  
      // Initialize Upsell elements and data
      this.initializeUpsell();
  
      // Setup add to cart functionality
      this.setupAddToCartListener();
  
      // Initialize button text on page load
      this.updateButtonText();
      this.updateDrawerButtonText();
  
      // Setup extra colors functionality after all other initialization
      if (this.allowAdditionalColors) {
        this.setupExtraColorsEventListeners();
      }
  
      // Dispatch an event to notify that the color picker has been initialized
      document.dispatchEvent(new CustomEvent('kit-color-picker-initialized', {
        detail: { colorPicker: this }
      }));
    }
  
    initializeUpsell() {
      this.upsellPopup = this.querySelector('.kit-upsell-popup');
      this.upsellOverlay = this.querySelector('.kit-color-picker-drawer__overlay'); // Reuse drawer overlay
  
      if (this.upsellPopup) {
        this.isUpsellEnabled = true; // If popup exists, upsell is enabled
        this.upsellLimit = parseInt(this.upsellPopup.dataset.upsellLimit) || 1;
        this.freeShippingThreshold = parseInt(this.upsellPopup.dataset.freeShippingThreshold) * 100 || 4900;
        this.currentKitPrice = parseInt(this.upsellPopup.dataset.currentKitPrice) * 100 || 0;
  
        // Get references to upsell elements
        this.upsellCloseButton = this.upsellPopup.querySelector('.kit-upsell-popup__close');
        this.upsellOptionInputs = this.upsellPopup.querySelectorAll('.kit-upsell-popup__option-input');
        this.upsellAddButton = this.upsellPopup.querySelector('.kit-upsell-popup__add-button');
        this.upsellDeclineButton = this.upsellPopup.querySelector('.kit-upsell-popup__decline-button');
        this.upsellProgressText = this.upsellPopup.querySelector('.kit-upsell-popup__progress-text');
        this.upsellProgressBarInner = this.upsellPopup.querySelector('.kit-upsell-popup__progress-bar-inner');
  
        // Setup upsell event listeners
        this.setupUpsellEventListeners();
      } else {
        this.isUpsellEnabled = false;
      }
    }
  
    setupUpsellEventListeners() {
      if (!this.upsellPopup) return;
  
      // Close button
      if (this.upsellCloseButton) {
        this.upsellCloseButton.addEventListener('click', () => this.hideUpsellPopup());
      }
  
      // Overlay click (shared with drawer)
      if (this.upsellOverlay) {
        // Ensure only one listener is attached or manage state carefully
        // For now, let's assume drawer's listener handles closing both drawer and potentially the upsell if it's open
        this.upsellOverlay.addEventListener('click', (e) => {
          // Check if the upsell popup is active before hiding
          if (this.upsellPopup.style.display === 'flex') {
            e.stopPropagation(); // Prevent drawer close logic if triggered by overlay click while upsell is open
            this.hideUpsellPopup();
          }
        });
      }
  
      // Option selection (radio/checkbox)
      this.upsellOptionInputs.forEach(input => {
        input.addEventListener('change', (e) => this.handleUpsellSelection(e.target));
      });
  
      // Add & Unlock button
      if (this.upsellAddButton) {
        this.upsellAddButton.addEventListener('click', () => {
          const upsellItemsToAdd = Array.from(this.selectedUpsellItems.keys()).map(variantId => ({
            id: variantId,
            quantity: 1,
            properties: { _upsell_item: true } // Add a property to identify upsell items if needed
          }));
          this.hideUpsellPopup();
          this.addKitToCart(upsellItemsToAdd);
        });
      }
  
      // Decline button
      if (this.upsellDeclineButton) {
        this.upsellDeclineButton.addEventListener('click', () => {
          this.hideUpsellPopup();
          this.addKitToCart(); // Add kit without upsells
        });
      }
    }
  
    handleUpsellSelection(selectedInput) {
      const variantId = selectedInput.value;
      const price = parseInt(selectedInput.dataset.price, 10);
  
      if (this.upsellLimit === 1) {
        // Radio button behavior
        this.selectedUpsellItems.clear(); // Clear previous selection
        if (selectedInput.checked) {
          this.selectedUpsellItems.set(variantId, price);
        }
        // Manually uncheck other radio buttons (though browser usually handles this)
        this.upsellOptionInputs.forEach(input => {
          if (input !== selectedInput) {
            input.checked = false;
            // Update visual state if using custom styled radios
            input.closest('.kit-upsell-popup__option')?.classList.remove('selected');
          } else {
            input.closest('.kit-upsell-popup__option')?.classList.toggle('selected', input.checked);
          }
        });
      } else {
        // Checkbox behavior
        if (selectedInput.checked) {
          if (this.selectedUpsellItems.size < this.upsellLimit) {
            this.selectedUpsellItems.set(variantId, price);
          } else {
            // Limit reached, prevent checking more
            selectedInput.checked = false;
            // Optionally provide user feedback (e.g., alert or message)
            console.warn(`Upsell limit (${this.upsellLimit}) reached.`);
            return; // Exit early
          }
        } else {
          this.selectedUpsellItems.delete(variantId);
        }
        // Update visual state for checkboxes
        selectedInput.closest('.kit-upsell-popup__option')?.classList.toggle('selected', selectedInput.checked);
      }
  
      this.updateUpsellProgress();
    }
  
    showUpsellPopup() {
      if (!this.upsellPopup || !this.upsellOverlay) return;
  
      // Ensure drawer is closed if open
      this.closeDrawer();
  
      // Reset selections
      this.selectedUpsellItems.clear();
      this.upsellOptionInputs.forEach(input => {
        input.checked = false;
        input.closest('.kit-upsell-popup__option')?.classList.remove('selected');
      });
  
      // Explicitly disable button initially after reset
      if (this.upsellAddButton) {
        this.upsellAddButton.disabled = true;
      }
  
      this.updateUpsellProgress(); // Update progress based on initial kit price
  
      // Show popup and overlay
      this.upsellPopup.style.display = 'flex';
      this.upsellOverlay.style.display = 'block';
      // Force reflow before opacity transition
      this.upsellOverlay.offsetHeight;
      this.upsellOverlay.style.opacity = '1';
  
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }
  
    hideUpsellPopup() {
      if (!this.upsellPopup || !this.upsellOverlay) return;
  
      this.upsellPopup.style.display = 'none';
      this.upsellOverlay.style.opacity = '0';
      setTimeout(() => {
        // Hide overlay only if drawer is also closed
        if (!this.drawer || !this.drawer.classList.contains('is-active')) {
          this.upsellOverlay.style.display = 'none';
        }
      }, 300); // Match transition duration
  
      document.body.style.overflow = ''; // Restore scrolling
    }
  
    // Helper function to format money
    formatMoney(valueInCents, currency = 'USD') {
      const value = valueInCents / 100;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
  
    updateUpsellProgress() {
      if (!this.isUpsellEnabled || !this.upsellProgressText || !this.upsellProgressBarInner || !this.upsellAddButton) return;
  
      const kitPriceCents = parseInt(this.currentKitPrice, 10) || 0;
      const thresholdCents = parseInt(this.freeShippingThreshold, 10) || 0;
  
      let selectedUpsellTotalCents = 0;
      this.selectedUpsellItems.forEach(price => {
        selectedUpsellTotalCents += price;
      });
  
      const totalCartValueCents = kitPriceCents + selectedUpsellTotalCents;
      let amountNeededCents = thresholdCents - totalCartValueCents;
      if (amountNeededCents < 0) amountNeededCents = 0;
  
      let progressPercent = 0;
      if (thresholdCents > 0) { // Avoid division by zero
        progressPercent = (totalCartValueCents / thresholdCents) * 100;
      }
      if (progressPercent > 100) progressPercent = 100;
      if (progressPercent < 0) progressPercent = 0;
  
      // Update Progress Text
      if (amountNeededCents > 0) {
        this.upsellProgressText.innerHTML = `<svg width="17" height="12" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M0 2.625H7.3125L7.875 3.75H0.5625L0 2.625ZM0.75 4.875H8.0625L8.625 6H1.3125L0.75 4.875ZM12.9375 10.875C13.56 10.875 14.0625 10.3725 14.0625 9.75C14.0625 9.1275 13.56 8.625 12.9375 8.625C12.315 8.625 11.8125 9.1275 11.8125 9.75C11.8125 10.3725 12.315 10.875 12.9375 10.875ZM14.0625 4.125H12.1875V6H15.5325L14.0625 4.125ZM5.4375 10.875C6.06 10.875 6.5625 10.3725 6.5625 9.75C6.5625 9.1275 6.06 8.625 5.4375 8.625C4.815 8.625 4.3125 9.1275 4.3125 9.75C4.3125 10.3725 4.815 10.875 5.4375 10.875ZM14.4375 3L16.6875 6V9.75H15.1875C15.1875 10.995 14.1825 12 12.9375 12C11.6925 12 10.6875 10.995 10.6875 9.75H7.6875C7.6875 10.995 6.675 12 5.4375 12C4.1925 12 3.1875 10.995 3.1875 9.75H1.6875V7.125H3.1875V8.25H3.7575C4.17 7.7925 4.77 7.5 5.4375 7.5C6.105 7.5 6.705 7.7925 7.1175 8.25H10.6875V1.5H1.6875C1.6875 0.6675 2.355 0 3.1875 0H12.1875V3H14.4375Z" fill="black"/>
                        </svg> You're ${this.formatMoney(amountNeededCents)} away from unlocking <strong>FREE SHIPPING!</strong>`;
      } else {
        this.upsellProgressText.innerHTML = `You've unlocked <strong>FREE SHIPPING!</strong>`;
      }
  
      // Update Progress Bar
      this.upsellProgressBarInner.style.width = `${progressPercent}%`;
  
      const upsellTotalFormatted = this.formatMoney(selectedUpsellTotalCents);
      
      const totalWithUpsell = kitPriceCents + selectedUpsellTotalCents;
      if (totalWithUpsell >= thresholdCents) {
        this.upsellAddButton.textContent = `Add & Unlock FREE Shipping ${upsellTotalFormatted}`;
      } else {
        this.upsellAddButton.textContent = `ADD TO CHECKOUT ${upsellTotalFormatted}`;
      }
  
      const hasSelection = this.selectedUpsellItems.size > 0;
      this.upsellAddButton.disabled = !hasSelection; 
    }
  
  
    setupEventListeners() {
      // Nail button clicks
      this.querySelectorAll('.kit-color-picker-builder-nail-button').forEach(button => {
        button.addEventListener('click', (e) => {
          if (!e.target.classList.contains('kit-color-picker-builder-nail-remove')) {
            this.openDrawer(button.dataset.colorIndex);
          }
        });
      });
  
      this.querySelectorAll('.kit-color-picker-builder-nail-remove').forEach(button => {
        button.addEventListener('click', (e) => {
          e.stopPropagation();
          const colorIndex = e.target.closest('.kit-color-picker-builder-nail-item').querySelector('.kit-color-picker-builder-nail-button').dataset.colorIndex;
          if (this.isExtraColorIndex(colorIndex)) {
            this.removeExtraColor(colorIndex);
          } else {
            this.removeColor(colorIndex);
          }
        });
      });
  
      const mainResetButton = this.querySelector('.kit-color-picker-builder-reset');
      if (mainResetButton) {
        mainResetButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.resetAllColors();
        });
      }
  
      // Drawer navigation
      if (this.drawer) {
        // Drawer nail buttons
        this.drawer.querySelectorAll('.kit-color-picker-drawer__nail-button').forEach(button => {
          button.addEventListener('click', (e) => {
            if (!e.target.classList.contains('kit-color-picker-drawer__nail-remove')) {
              this.currentColorIndex = button.dataset.colorIndex;
              this.highlightCurrentSlot();
            }
          });
        });
  
        // Drawer remove buttons
        this.drawer.querySelectorAll('.kit-color-picker-drawer__nail-remove').forEach(button => {
          button.addEventListener('click', (e) => {
            e.stopPropagation();
            const colorIndex = e.target.closest('.kit-color-picker-drawer__nail-item').querySelector('.kit-color-picker-drawer__nail-button').dataset.colorIndex;
            if (this.isExtraColorIndex(colorIndex)) {
              this.removeExtraColor(colorIndex);
            } else {
              this.removeColor(colorIndex);
            }
          });
        });
  
        // Close drawer buttons
        const closeButton = this.drawer.querySelector('.kit-color-picker-drawer__close');
        if (closeButton) {
          closeButton.addEventListener('click', () => this.closeDrawer());
        }
  
        // Overlay click event - MODIFIED TO HANDLE UPSELL
        const overlay = this.querySelector('.kit-color-picker-drawer__overlay');
        if (overlay) {
          overlay.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.upsellPopup && this.upsellPopup.style.display === 'flex') {
              this.hideUpsellPopup();
            } else if (this.drawer && this.drawer.classList.contains('is-active')) {
              this.closeDrawer();
            }
          });
        }
  
        const applyButton = this.drawer.querySelector('.kit-color-picker-drawer__apply');
        if (applyButton) {
          applyButton.addEventListener('click', () => {
  
            const allColorsSelected = this.selectedColors.size === this.selectableColors;
            
            if (allColorsSelected) {
  
              if (this.isUpsellEnabled) {
  
                this.closeDrawer();   
                this.showUpsellPopup(); 
              } else {
  
                this.closeDrawer(); 
                this.addKitToCart(); 
              }
            } else {
  
              this.closeDrawer();
            }
          });
        }
  
        this.drawer.querySelectorAll('.skin-tone-button').forEach(button => {
          button.addEventListener('click', (e) => this.handleSkinToneChange(e));
        });
        
        // Use a flag to prevent duplicate color selection events
        this.isProcessingColorSelection = false;
        
        // Single event handler for color option clicks using event delegation
        this.drawer.addEventListener('click', (e) => {
          const colorOption = e.target.closest('.color-option');
          if (colorOption && !this.isProcessingColorSelection) {
            this.isProcessingColorSelection = true;
            try {
              const variantId = colorOption.dataset.variantId;
              const colorInfo = JSON.parse(colorOption.dataset.colorInfo);
              const colorImage = colorOption.querySelector('.color-option__image')?.dataset.firstImage;
              this.selectColor(variantId, colorInfo, colorOption, colorImage);
            } catch (error) {
              console.error('Error selecting color:', error);
            } finally {
              // Reset the flag after a short delay to prevent race conditions
              setTimeout(() => {
                this.isProcessingColorSelection = false;
              }, 100);
            }
          }
        });
        
        // Remove direct event listeners on color options to prevent duplicates
        // We'll use event delegation instead (above)
  
        const drawerResetButton = this.drawer.querySelector('.kit-color-picker-drawer__reset');
        if (drawerResetButton) {
          drawerResetButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.resetAllColors();
          });
        }
  
        this.setupImageSwitcher();
        this.setupSorting();
        this.setupColorFilterEventListeners();
        this.setupStickySelectedColors();
      }
    }
  
    setupColorFilterEventListeners() {
      if (!this.isPopupStyle) return;
  
      // Category filter buttons
      const categoryButtons = this.drawer.querySelectorAll('[data-filter-type="category"]');
      categoryButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const filterValue = button.dataset.filterValue;
          const isCurrentlyActive = button.classList.contains('kit-color-picker-builder__color-filter-btn--active');
          
          // Always clear all active states first
          categoryButtons.forEach(btn => btn.classList.remove('kit-color-picker-builder__color-filter-btn--active'));
          
          if (isCurrentlyActive && filterValue !== 'all') {
            // If clicking the currently active filter (except 'all'), deselect it and activate 'all'
            const allButton = this.drawer.querySelector('[data-filter-value="all"]');
            if (allButton) {
              allButton.classList.add('kit-color-picker-builder__color-filter-btn--active');
            }
            this.currentCategoryFilter = 'all';
          } else {
            // Select the clicked filter
            button.classList.add('kit-color-picker-builder__color-filter-btn--active');
            this.currentCategoryFilter = filterValue;
          }
          
          this.applyCombinedFilters();
        });
      });
  
      // Color filter swatches
      const colorSwatches = this.drawer.querySelectorAll('[data-filter-type="color"]');
      colorSwatches.forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          const filterValue = swatch.dataset.filterValue;
          const isCurrentlyActive = swatch.classList.contains('kit-color-picker-builder__color-filter-swatch--active');
          
          // If clicking the currently active swatch, deselect it
          if (isCurrentlyActive) {
            swatch.classList.remove('kit-color-picker-builder__color-filter-swatch--active');
            this.currentColorFilter = null;
          } else {
            // Update active state
            colorSwatches.forEach(sw => sw.classList.remove('kit-color-picker-builder__color-filter-swatch--active'));
            swatch.classList.add('kit-color-picker-builder__color-filter-swatch--active');
            
            // Apply filter
            this.currentColorFilter = filterValue;
          }
          
          this.applyCombinedFilters();
        });
      });
  
      // Setup drag functionality for filter sliders
      this.setupFilterSliderDrag();
    }
  
    setupStickySelectedColors() {
      // Only apply to popup style on mobile
      if (!this.isPopupStyle || !this.isMobile()) return;
  
      const drawerBody = this.drawer.querySelector('.kit-color-picker-drawer__body');
      const selectedColorsSection = this.drawer.querySelector('.kit-color-picker-drawer__selected-colors--popup');
      const colorFilterSection = this.drawer.querySelector('.kit-color-picker-builder__color-filter');
      
      if (!drawerBody || !selectedColorsSection) return;
  
      let scrollTimeout;
  
      const handleScroll = () => {
        const scrollTop = drawerBody.scrollTop;
        
        // Add/remove sticky classes based on scroll
        if (scrollTop > 10) {
          selectedColorsSection.classList.add('is-sticky');
          drawerBody.classList.add('is-sticky');
          if (colorFilterSection) {
            colorFilterSection.classList.add('is-sticky');
            drawerBody.classList.add('is-sticky');
          }
        } else {
          selectedColorsSection.classList.remove('is-sticky');
          drawerBody.classList.remove('is-sticky');
          if (colorFilterSection) {
            colorFilterSection.classList.remove('is-sticky');
            drawerBody.classList.remove('is-sticky');
          }
        }
      };
  
      // Listen for scroll events on the drawer body
      drawerBody.addEventListener('scroll', handleScroll, { passive: true });
    }
  
    setupFilterSliderDrag() {
      const categorySlider = this.drawer.querySelector('[id^="CategoryFilterSlider-"]');
      const colorSlider = this.drawer.querySelector('[id^="ColorFilterSlider-"]');
      
      [categorySlider, colorSlider].forEach(slider => {
        if (!slider) return;
        
        let isPressed = false;
        let startX = 0;
        let scrollLeft = 0;
        
        const onMouseDown = (e) => {
          // Don't start drag if clicking on a button
          if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
            return;
          }
          
          isPressed = true;
          startX = e.pageX - slider.offsetLeft;
          scrollLeft = slider.scrollLeft;
          slider.style.cursor = 'grabbing';
          slider.style.userSelect = 'none';
          e.preventDefault();
        };
        
        const onMouseMove = (e) => {
          if (!isPressed) return;
          e.preventDefault();
          const x = e.pageX - slider.offsetLeft;
          const walk = (startX - x) * 2; // Multiply by 2 for faster scrolling
          slider.scrollLeft = scrollLeft + walk;
        };
        
        const onMouseUp = () => {
          isPressed = false;
          slider.style.cursor = 'grab';
          slider.style.removeProperty('user-select');
        };
        
        // Add event listeners
        slider.addEventListener('mousedown', onMouseDown);
        slider.addEventListener('mousemove', onMouseMove);
        slider.addEventListener('mouseup', onMouseUp);
        slider.addEventListener('mouseleave', onMouseUp);
        
        // Prevent drag on images and other elements
        slider.addEventListener('dragstart', (e) => e.preventDefault());
      });
    }
  
    applyCombinedFilters() {
      const colorOptions = this.drawer.querySelectorAll('.color-option');
      
      colorOptions.forEach(option => {
        const colorInfo = JSON.parse(option.dataset.colorInfo);
        const productTags = colorInfo.tags || [];
        const productType = colorInfo.product_type || '';
        const productVendor = colorInfo.vendor || '';
        const productCollections = colorInfo.collections || [];
        
        let showProduct = true;
        
        // Apply category filter
        if (this.currentCategoryFilter && this.currentCategoryFilter !== 'all') {
          let hasCategoryMatch = false;
          const filterValue = this.currentCategoryFilter;
            
  
          
          const categoryTag = filterValue.replace('-', '_');
          hasCategoryMatch = productTags.some(tag => 
            tag.toLowerCase().includes(categoryTag.toLowerCase())
          );
          
          if (!hasCategoryMatch) {
            hasCategoryMatch = productType.toLowerCase().includes(filterValue.toLowerCase());
          }
          
          if (!hasCategoryMatch && productCollections.length > 0) {
            hasCategoryMatch = productCollections.some(collection => 
              collection.handle && collection.handle.toLowerCase().includes(filterValue.toLowerCase())
            );
          }
          
          if (!hasCategoryMatch && productCollections.length > 0) {
            hasCategoryMatch = productCollections.some(collection => 
              collection.title && collection.title.toLowerCase().includes(filterValue.toLowerCase())
            );
          }
          
          if (!hasCategoryMatch) {
            if (filterValue === 'best-seller') {
              hasCategoryMatch = productTags.some(tag => 
                tag.toLowerCase().includes('bestseller') || 
                tag.toLowerCase().includes('best seller') 
              );
            } else if (filterValue === 'thermal') {
              hasCategoryMatch = productTags.some(tag => 
                tag.toLowerCase().includes('color_color changing') ||
                tag.toLowerCase().includes('color changing') ||
                tag.toLowerCase().includes('thermal')
              );
            } else if (filterValue === 'flower') {
              hasCategoryMatch = productTags.some(tag => 
                tag.toLowerCase().includes('flower') 
              );
            } else if (filterValue === 'new') {
              hasCategoryMatch = productTags.some(tag => 
                tag.toLowerCase().includes('new') ||
                tag.toLowerCase().includes('latest') ||
                tag.toLowerCase().includes('newest')
              );
            } else if (filterValue === 'glow-in-dark') {
              hasCategoryMatch = productTags.some(tag => 
                tag.toLowerCase().includes('glow') ||
                tag.toLowerCase().includes('glow in dark') ||
                tag.toLowerCase().includes('glow-in-dark')
              );
            }
          }
          
          if (!hasCategoryMatch) {
            showProduct = false;
          }
        }
        
        // Apply color filter (only if a color is selected)
        if (this.currentColorFilter && showProduct) {
          const hasColorTag = productTags.some(tag => 
            tag.includes('color_') && tag.replace('color_', '') === this.currentColorFilter
          );
          
          if (!hasColorTag) {
            showProduct = false;
          }
        }
        
        // Show/hide the product
        option.style.display = showProduct ? '' : 'none';
      });
      
      // Update grid layout after filtering
      this.updateGridLayout();
    }
  
  
  
    updateGridLayout() {
      // Force a reflow to ensure proper grid layout
      const grid = this.drawer.querySelector('.kit-color-picker-drawer__grid');
      if (grid) {
        grid.style.display = 'none';
        grid.offsetHeight; // Trigger reflow
        grid.style.display = '';
      }
    }
  
    isMobile() {
      return window.innerWidth <= 768;
    }
  
    openDrawer(colorIndex) {
      if (!this.drawer || !this.upsellOverlay) { // Check for overlay too
        console.warn('Cannot open drawer: drawer or overlay element not found');
        return;
      }
  
      console.log('Opening drawer for color index:', colorIndex);
  
      // Close upsell if it's open
      if (this.upsellPopup && this.upsellPopup.style.display === 'flex') {
        this.hideUpsellPopup();
      }
  
      // Make sure the overlay has the correct event listener (it might have been changed by upsell)
      // Re-attaching might be simpler than complex state management
      this.upsellOverlay.replaceWith(this.upsellOverlay.cloneNode(true));
      this.upsellOverlay = this.querySelector('.kit-color-picker-drawer__overlay'); 
      this.upsellOverlay.addEventListener('click', (e) => { 
        e.stopPropagation();
        if (this.drawer.classList.contains('is-active')) {
          this.closeDrawer();
        }
      });
  
      // For regular color indices (numeric), parse as integer; for extra colors, keep as string
      if (typeof colorIndex === 'string' && colorIndex.startsWith('extra-')) {
        this.currentColorIndex = colorIndex;
      } else {
        this.currentColorIndex = parseInt(colorIndex);
      }
      
      this.drawer.classList.add('is-active');
  
      this.upsellOverlay.style.display = 'block';
  
      // For popup style, we don't need to wait for the offset height since the animation is different
      if (!this.isPopupStyle) {
        this.upsellOverlay.offsetHeight;
      }
  
      this.upsellOverlay.style.opacity = '1';
  
      document.body.style.overflow = 'hidden';
      
      // On mobile with popup style, also prevent touch scrolling on the body
      if (this.isPopupStyle && this.isMobile()) {
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      }
      this.highlightCurrentSlot();
      this.updateDrawerColorSelector();
      this.updateDrawerButtonText();
      
      // Reset scroll position and sticky state when opening drawer
      if (this.isPopupStyle && this.isMobile()) {
        const drawerBody = this.drawer.querySelector('.kit-color-picker-drawer__body');
        const selectedColorsSection = this.drawer.querySelector('.kit-color-picker-drawer__selected-colors--popup');
        const colorFilterSection = this.drawer.querySelector('.kit-color-picker-builder__color-filter');
        
        if (drawerBody) {
          drawerBody.scrollTop = 0;
        }
        
        if (selectedColorsSection) {
          selectedColorsSection.classList.remove('is-sticky');
          drawerBody.classList.remove('is-sticky');
        }
        if (colorFilterSection) {
          colorFilterSection.classList.remove('is-sticky');
          drawerBody.classList.remove('is-sticky');
        }
      }
      
      // Debug: Check if progress bar exists for popup style
      if (this.isPopupStyle) {
        const progressBar = this.drawer.querySelector('.kit-color-picker-drawer__progress-bar');
        const progressFill = this.drawer.querySelector('.kit-color-picker-drawer__progress-fill');
        console.log('Progress bar elements:', { progressBar, progressFill });
        
        // Force initial progress update
        setTimeout(() => {
          this.updateProgressBar();
        }, 100);
      } 
    }
  
    closeDrawer() {
      if (!this.drawer || !this.upsellOverlay) return; 
  
      this.drawer.classList.remove('is-active');
  
      // Reset sticky state on mobile popup style
      if (this.isPopupStyle && this.isMobile()) {
        const selectedColorsSection = this.drawer.querySelector('.kit-color-picker-drawer__selected-colors--popup');
        const colorFilterSection = this.drawer.querySelector('.kit-color-picker-builder__color-filter');
        
        if (selectedColorsSection) {
          selectedColorsSection.classList.remove('is-sticky');
        }
        if (colorFilterSection) {
          colorFilterSection.classList.remove('is-sticky');
        }
      }
  
      if (!this.upsellPopup || this.upsellPopup.style.display !== 'flex') {
        this.upsellOverlay.style.opacity = '0';
        setTimeout(() => {
          this.upsellOverlay.style.display = 'none';
        }, 400); 
        
        // Restore body scroll
        document.body.style.overflow = '';
        
        // On mobile with popup style, restore body position
        if (this.isPopupStyle && this.isMobile()) {
          document.body.style.position = '';
          document.body.style.width = '';
        }
      }
    }
  
    handleSkinToneChange(event) {
      if (!this.drawer) return;
      const tone = event.target.dataset.tone;
      this.drawer.querySelectorAll('.skin-tone-button').forEach(btn => {
        btn.classList.toggle('is-active', btn === event.target);
      });
    }
  
    updateDrawerColorSelector() {
      // Update the drawer's color selector to match the main display for regular colors
      for (let i = 1; i <= this.selectableColors; i++) {
        const mainNailItem = this.querySelector(`.nail-color-${i}`);
        const drawerNailItem = this.drawer.querySelector(`.drawer-nail-color-${i}`);
  
        if (mainNailItem && drawerNailItem) {
          this.syncNailItemStates(mainNailItem, drawerNailItem);
        }
      }
  
      // Update the drawer's color selector for extra colors
      // First handle the initial extra color slot
      const mainExtraItem = this.querySelector('.nail-color-extra-colors');
      const drawerExtraItem = this.drawer.querySelector('.drawer-nail-color-extra-colors');
      
      if (mainExtraItem && drawerExtraItem) {
        this.syncNailItemStates(mainExtraItem, drawerExtraItem);
      }
      
      // Then handle any additional dynamically added extra colors
      for (let i = 1; i <= this.extraColorCount; i++) {
        const mainExtraItem = this.querySelector(`.nail-color-extra-colors-${i}`);
        const drawerExtraItem = this.drawer.querySelector(`.drawer-nail-color-extra-colors-${i}`);
        
        if (mainExtraItem && drawerExtraItem) {
          this.syncNailItemStates(mainExtraItem, drawerExtraItem);
        }
      }
  
      // Highlight the current selection
      this.highlightCurrentSlot();
      
      // Update progress bar for popup style
      if (this.isPopupStyle) {
        this.updateProgressBar();
      }
    }
  
    // Helper method to sync states between main and drawer nail items
    syncNailItemStates(mainItem, drawerItem) {
      const mainButton = mainItem.querySelector('.kit-color-picker-builder-nail-button');
      const drawerButton = drawerItem.querySelector('.kit-color-picker-drawer__nail-button');
  
      const mainRemove = mainItem.querySelector('.kit-color-picker-builder-nail-remove');
      const drawerRemove = drawerItem.querySelector('.kit-color-picker-drawer__nail-remove');
  
      const mainColor = mainItem.querySelector('.kit-color-picker-builder-nail-button-color');
      const drawerColor = drawerItem.querySelector('.kit-color-picker-drawer__nail-color');
  
      // Copy classes for selection state
      drawerButton.classList.toggle('selected', mainButton.classList.contains('selected'));
      drawerItem.classList.toggle('has-color', mainItem.classList.contains('has-color'));
  
      // Copy visibility of remove button
      drawerRemove.classList.toggle('hidden', mainRemove.classList.contains('hidden'));
  
      // Copy visibility of free badge or discount badge
      const mainFreeBadge = mainItem.querySelector('.kit-color-picker-builder-nail-free-badge');
      const drawerFreeBadge = drawerItem.querySelector('.kit-color-picker-builder-nail-free-badge');
      if (mainFreeBadge && drawerFreeBadge) {
        drawerFreeBadge.classList.toggle('hidden', mainFreeBadge.classList.contains('hidden'));
      }
  
      const mainDiscountBadge = mainItem.querySelector('.kit-color-picker-builder-nail-discount-badge');
      const drawerDiscountBadge = drawerItem.querySelector('.kit-color-picker-builder-nail-discount-badge');
      if (mainDiscountBadge && drawerDiscountBadge) {
        drawerDiscountBadge.classList.toggle('hidden', mainDiscountBadge.classList.contains('hidden'));
      }
  
      // Copy background styles - exactly matching the main display
      if (mainColor && drawerColor) {
        // Copy all relevant style properties
        drawerColor.style.backgroundImage = mainColor.style.backgroundImage;
        drawerColor.style.backgroundColor = mainColor.style.backgroundColor;
        drawerColor.style.backgroundSize = 'cover';
        drawerColor.style.backgroundPosition = 'center';
        drawerColor.style.backgroundRepeat = 'no-repeat';
      }
    }
  
    highlightCurrentSlot() {
      if (!this.drawer) return;
  
      // Remove highlight from all slots in drawer
      this.drawer.querySelectorAll('.kit-color-picker-drawer__nail-item').forEach(item => {
        item.classList.remove('current-selection');
      });
  
      // Remove highlight from all slots in main display
      this.querySelectorAll('.kit-color-picker-builder-nail-item').forEach(item => {
        item.classList.remove('current-selection');
      });
  
      // Add highlight to current slot in drawer
      if (this.currentColorIndex) {
        let currentDrawerSlotSelector;
        
        if (this.isExtraColorIndex(this.currentColorIndex)) {
          const extraNumber = this.currentColorIndex.split('-')[1];
          currentDrawerSlotSelector = extraNumber === '0' 
            ? '.drawer-nail-color-extra-colors' 
            : `.drawer-nail-color-extra-colors-${extraNumber}`;
        } else {
          currentDrawerSlotSelector = `.drawer-nail-color-${this.currentColorIndex}`;
        }
        
        const currentDrawerSlot = this.drawer.querySelector(currentDrawerSlotSelector);
        if (currentDrawerSlot) {
          currentDrawerSlot.classList.add('current-selection');
        }
  
        // Add highlight to current slot in main display
        let currentMainSlotSelector;
        
        if (this.isExtraColorIndex(this.currentColorIndex)) {
          const extraNumber = this.currentColorIndex.split('-')[1];
          currentMainSlotSelector = extraNumber === '0' 
            ? '.nail-color-extra-colors' 
            : `.nail-color-extra-colors-${extraNumber}`;
        } else {
          currentMainSlotSelector = `.nail-color-${this.currentColorIndex}`;
        }
        
        const currentMainSlot = this.querySelector(currentMainSlotSelector);
        if (currentMainSlot) {
          currentMainSlot.classList.add('current-selection');
        }
      }
    }
  
    selectColor(variantId, colorInfo, colorOption, imageUrl) {
      // Check if this is an extra color selection
      const isExtraColor = typeof this.currentColorIndex === 'string' && this.currentColorIndex.startsWith('extra-');
      
      if (isExtraColor) {
        // Handle extra color selection
        return this.selectExtraColor(variantId, colorInfo, colorOption, imageUrl);
      }
      
      // Regular color selection logic
      // Update selected colors map
      this.selectedColors.set(this.currentColorIndex, {
        variantId,
        ...colorInfo,
        imageUrl // Store the image URL for later use
      });
  
      // Update nail display
      const nailItem = this.querySelector(`.nail-color-${this.currentColorIndex}`);
      if (!nailItem) return;
  
      const nailButton = nailItem.querySelector('.kit-color-picker-builder-nail-button');
      const removeButton = nailItem.querySelector('.kit-color-picker-builder-nail-remove');
      if (!nailButton || !removeButton) return;
  
      // Add selected class to the nail button
      nailButton.classList.add('selected');
      nailItem.classList.add('has-color');
      removeButton.classList.remove('hidden');
  
      // Set the nail color
      const colorDisplay = nailButton.querySelector('.kit-color-picker-builder-nail-button-color');
      if (!colorDisplay) return;
  
      // Clear any existing background
      colorDisplay.style.backgroundImage = '';
      colorDisplay.style.backgroundColor = '';
  
      let finalImageUrl = '';
  
      if (imageUrl) {
        finalImageUrl = imageUrl;
      }
  
      else if (colorOption) {
        const colorImage = colorOption.querySelector('.color-option__image');
        if (colorImage && colorImage.dataset.firstImage) {
          finalImageUrl = colorImage.dataset.firstImage;
        }
      }
  
      if (!finalImageUrl && colorInfo.featured_image) {
        finalImageUrl = colorInfo.featured_image;
      }
  
      // Apply the image or color
      if (finalImageUrl) {
        if (finalImageUrl.includes('&')) {
          finalImageUrl = finalImageUrl.split('&')[0];
        }
  
        colorDisplay.style.backgroundImage = `url("${finalImageUrl}")`;
        colorDisplay.style.backgroundSize = 'cover';
        colorDisplay.style.backgroundPosition = 'center';
        colorDisplay.style.backgroundRepeat = 'no-repeat';
      } else {
        colorDisplay.style.backgroundColor = colorInfo.color || '#ff61d3';
      }
  
      this.updateDrawerColorSelector();
      this.updateButtonText();
      this.updateDrawerButtonText();
  
      // Check if we just selected the last regular color
      const justCompletedRegularColors = this.selectedColors.size === this.selectableColors;
      
      // Advance to the next slot - if we're at the last regular slot and additional colors
      // are allowed, this will move to the first extra color slot
      this.advanceToNextEmptySlot();
      
      const isInDrawer = this.drawer && this.drawer.classList.contains('is-active');
      
      if (isInDrawer) {
        return;
      }
      
      // If we just completed all regular colors and additional colors are allowed, 
      // don't close the drawer, as we're transitioning to extra colors
      if (justCompletedRegularColors && this.allowAdditionalColors) {
        // Drawer should remain open as advanceToNextEmptySlot will have opened the drawer
        // for the extra color if needed
        return;
      }
      
      this.closeDrawer();
    }
  
    selectExtraColor(variantId, colorInfo, colorOption, imageUrl) {
      console.log('Selecting extra color:', this.currentColorIndex);
      const extraIndex = this.currentColorIndex;
      
      // Skip if this is a duplicate call
      if (this.extraColors.has(extraIndex) && 
          this.extraColors.get(extraIndex).variantId === variantId) {
        console.log('Skipping duplicate extra color selection');
        return;
      }
      
      // Check if this is the initial extra color (at index 0)
      const extraIndexNumber = parseInt(extraIndex.replace('extra-', ''));
      const targetSelector = extraIndexNumber === 0 
        ? '.nail-color-extra-colors' 
        : `.nail-color-extra-colors-${extraIndexNumber}`;
      
      console.log('Target selector for extra color:', targetSelector);
      
      // Add to extra colors map
      this.extraColors.set(extraIndex, {
        variantId,
        ...colorInfo,
        imageUrl
      });
      
      // Update the extra color nail
      const nailItem = this.querySelector(targetSelector);
      if (!nailItem) {
        console.error('Could not find nail item for', targetSelector);
        return;
      }
  
      const nailButton = nailItem.querySelector('.kit-color-picker-builder-nail-button');
      const removeButton = nailItem.querySelector('.kit-color-picker-builder-nail-remove');
      if (!nailButton || !removeButton) {
        console.error('Could not find nail button or remove button');
        return;
      }
  
      // Add selected class
      nailButton.classList.add('selected');
      nailItem.classList.add('has-color');
      removeButton.classList.remove('hidden');
  
      // Set the nail color
      const colorDisplay = nailButton.querySelector('.kit-color-picker-builder-nail-button-color');
      if (!colorDisplay) {
        console.error('Could not find color display element');
        return;
      }
  
      // Update color display similar to regular colors
      colorDisplay.style.backgroundImage = '';
      colorDisplay.style.backgroundColor = '';
  
      let finalImageUrl = '';
  
      if (imageUrl) {
        finalImageUrl = imageUrl;
      } else if (colorOption) {
        const colorImage = colorOption.querySelector('.color-option__image');
        if (colorImage && colorImage.dataset.firstImage) {
          finalImageUrl = colorImage.dataset.firstImage;
        }
      }
  
      if (!finalImageUrl && colorInfo.featured_image) {
        finalImageUrl = colorInfo.featured_image;
      }
  
      // Apply the image or color
      if (finalImageUrl) {
        if (finalImageUrl.includes('&')) {
          finalImageUrl = finalImageUrl.split('&')[0];
        }
  
        colorDisplay.style.backgroundImage = `url("${finalImageUrl}")`;
        colorDisplay.style.backgroundSize = 'cover';
        colorDisplay.style.backgroundPosition = 'center';
        colorDisplay.style.backgroundRepeat = 'no-repeat';
      } else {
        colorDisplay.style.backgroundColor = colorInfo.color || '#ff61d3';
      }
  
      // Update drawer
      this.updateDrawerColorSelector();
      
      // Add a new extra color slot if not already added and if this isn't the result of a duplicate event
      if (extraIndexNumber === this.extraColorCount) {
        // Only add a new slot if we're working with the most recent slot
        this.addExtraColorSlot();
        
        // Move to the next extra color slot
        const nextExtraIndex = `extra-${this.extraColorCount}`;
        this.currentColorIndex = nextExtraIndex;
        this.highlightCurrentSlot();
        
        // Keep the drawer open since we're adding another color
        return;
      }
    }
  
    addExtraColorSlot() {
      this.extraColorCount++;
      const nextExtraIndex = this.extraColorCount;
      console.log('Adding extra color slot:', nextExtraIndex);
      
      // Create a new empty extra color slot
      this.createExtraColorSlot(nextExtraIndex);
    }
  
    removeExtraColor(extraIndex) {
      console.log('Removing extra color:', extraIndex);
      
      // Extract the number from the extraIndex (e.g., 'extra-2' → 2)
      const extraIndexNumber = parseInt(extraIndex.replace('extra-', ''));
      
      // Check if we have any extra colors before proceeding
      if (this.extraColorCount === 0 && extraIndexNumber === 0) {
        // If there's only the initial slot and no other extra colors, just clear it
        this.clearExtraColorSlot(extraIndex);
        return;
      }
      
      // Special handling if removing the initial extra color
      if (extraIndexNumber === 0) {
        // First, clear the initial slot
        this.clearExtraColorSlot(extraIndex);
        
        // If we have additional color slots, we need to remove all of them
        if (this.extraColorCount > 0) {
          // Remove all the additional slots
          for (let i = 1; i <= this.extraColorCount; i++) {
            const selector = `.nail-color-extra-colors-${i}`;
            const slotElement = this.querySelector(selector);
            if (slotElement) {
              slotElement.remove();
            }
            
            const drawerSelector = `.drawer-nail-color-extra-colors-${i}`;
            const drawerElement = this.drawer.querySelector(drawerSelector);
            if (drawerElement) {
              drawerElement.remove();
            }
            
            // Clean up the data
            this.extraColors.delete(`extra-${i}`);
          }
          
          // Reset the extra color count
          this.extraColorCount = 0;
        }
        
        // Update drawer
        this.updateDrawerColorSelector();
        return;
      }
      
      // For non-initial slots, use the existing logic for removal and renumbering
      
      // Get nail item based on extra index
      const nailItemSelector = `.nail-color-extra-colors-${extraIndexNumber}`;
      
      const nailItem = this.querySelector(nailItemSelector);
      if (!nailItem) {
        console.error('Could not find nail item:', nailItemSelector);
        return;
      }
      
      // Store information about all extra color slots
      const extraSlots = [];
      // Start from index 1 since we're dealing with non-initial slots
      for (let i = 1; i <= this.extraColorCount; i++) {
        if (i !== extraIndexNumber) { // Skip the one being removed
          const selector = `.nail-color-extra-colors-${i}`;
          const slotElement = this.querySelector(selector);
          const drawerSelector = `.drawer-nail-color-extra-colors-${i}`;
          const drawerElement = this.drawer.querySelector(drawerSelector);
          
          if (slotElement) {
            extraSlots.push({
              index: i,
              element: slotElement,
              drawerElement: drawerElement,
              hasColor: slotElement.classList.contains('has-color'),
              colorData: this.extraColors.get(`extra-${i}`)
            });
          }
        }
      }
      
      // Remove the color data for the slot we're removing
      this.extraColors.delete(extraIndex);
      
      // Remove DOM elements for all slots from extraIndexNumber onwards
      for (let i = extraIndexNumber; i <= this.extraColorCount; i++) {
        const selector = `.nail-color-extra-colors-${i}`;
        const slotElement = this.querySelector(selector);
        if (slotElement) {
          slotElement.remove();
        }
        
        const drawerSelector = `.drawer-nail-color-extra-colors-${i}`;
        const drawerElement = this.drawer.querySelector(drawerSelector);
        if (drawerElement) {
          drawerElement.remove();
        }
      }
      
      // Decrement extra color count
      this.extraColorCount--;
      
      // If we removed all extra slots, we're done
      if (this.extraColorCount < 1) {
        this.updateDrawerColorSelector();
        return;
      }
      
      // Rebuild the slots with correct numbering
      extraSlots.sort((a, b) => a.index - b.index);
      
      for (let i = 1; i <= this.extraColorCount; i++) {
        const slotData = extraSlots[i - 1];
        if (slotData) {
          this.createExtraColorSlot(i, slotData.colorData, slotData.hasColor);
        }
      }
      
      // Update drawer
      this.updateDrawerColorSelector();
    }
  
    // Add a helper method to clear an extra color slot without removing it
    clearExtraColorSlot(extraIndex) {
      const extraIndexNumber = parseInt(extraIndex.replace('extra-', ''));
      const selector = extraIndexNumber === 0 
        ? '.nail-color-extra-colors' 
        : `.nail-color-extra-colors-${extraIndexNumber}`;
      
      const slotElement = this.querySelector(selector);
      if (!slotElement) {
        console.error('Could not find slot to clear:', selector);
        return;
      }
      
      // Get the button and display elements
      const button = slotElement.querySelector('.kit-color-picker-builder-nail-button');
      const removeButton = slotElement.querySelector('.kit-color-picker-builder-nail-remove');
      const colorDisplay = button?.querySelector('.kit-color-picker-builder-nail-button-color');
      
      // Clear the UI
      if (button && removeButton && colorDisplay) {
        button.classList.remove('selected');
        slotElement.classList.remove('has-color');
        removeButton.classList.add('hidden');
        colorDisplay.style.backgroundImage = '';
        colorDisplay.style.backgroundColor = '';
      }
      
      // Clear the drawer slot too
      const drawerSelector = extraIndexNumber === 0 
        ? '.drawer-nail-color-extra-colors' 
        : `.drawer-nail-color-extra-colors-${extraIndexNumber}`;
      
      const drawerElement = this.drawer.querySelector(drawerSelector);
      if (drawerElement) {
        const drawerButton = drawerElement.querySelector('.kit-color-picker-drawer__nail-button');
        const drawerRemoveButton = drawerElement.querySelector('.kit-color-picker-drawer__nail-remove');
        const drawerColorDisplay = drawerButton?.querySelector('.kit-color-picker-drawer__nail-color');
        
        if (drawerButton && drawerRemoveButton && drawerColorDisplay) {
          drawerButton.classList.remove('selected');
          drawerElement.classList.remove('has-color');
          drawerRemoveButton.classList.add('hidden');
          drawerColorDisplay.style.backgroundImage = '';
          drawerColorDisplay.style.backgroundColor = '';
        }
      }
      
      // Remove from the extra colors map
      this.extraColors.delete(extraIndex);
    }
  
    // Method to create an extra color slot with specified data
    createExtraColorSlot(slotIndex, colorData = null, hasColor = false) {
      console.log('Creating extra color slot:', slotIndex, colorData, hasColor);
      
      const nailList = this.querySelector('.kit-color-picker-builder-nail-list');
      const drawerNailList = this.querySelector('.kit-color-picker-drawer__nail-list');
      
      if (!nailList || !drawerNailList) {
        console.error('Could not find nail lists');
        return;
      }
      
      // Get the discount badge text from the initial extra color slot
      const extraColorsDiscount = this.querySelector('.nail-color-extra-colors .kit-color-picker-builder-nail-discount-badge');
      const discountText = extraColorsDiscount ? extraColorsDiscount.textContent : '';
      
      // Calculate the color number
      const colorNumber = this.selectableColors + slotIndex + 1;
      
      // Create new nail item
      const newNailItem = document.createElement('li');
      newNailItem.className = `kit-color-picker-builder-nail-item nail-color-extra-colors-${slotIndex}`;
      if (hasColor) {
        newNailItem.classList.add('has-color');
      }
      
      newNailItem.innerHTML = `
        <button class="kit-color-picker-builder-nail-button${hasColor ? ' selected' : ''}" data-color-index="extra-${slotIndex}">
          <div class="kit-color-picker-builder-nail-discount-badge">${discountText}</div>
          <span class="kit-color-picker-builder-nail-button-color"></span>
          <button class="kit-color-picker-builder-nail-remove${hasColor ? '' : ' hidden'}" aria-label="Remove color">×</button>
        </button>
        <div class="kit-color-picker-builder-nail-label">Color ${colorNumber}</div>
      `;
      nailList.appendChild(newNailItem);
      
      // Create drawer nail item
      const newDrawerNailItem = document.createElement('li');
      newDrawerNailItem.className = `kit-color-picker-drawer__nail-item drawer-nail-color-extra-colors-${slotIndex}`;
      if (hasColor) {
        newDrawerNailItem.classList.add('has-color');
      }
      
      newDrawerNailItem.innerHTML = `
        <button class="kit-color-picker-drawer__nail-button${hasColor ? ' selected' : ''}" data-color-index="extra-${slotIndex}">
          <div class="kit-color-picker-builder-nail-discount-badge">${discountText}</div>
          <span class="kit-color-picker-drawer__nail-color"></span>
          <button class="kit-color-picker-drawer__nail-remove${hasColor ? '' : ' hidden'}" aria-label="Remove color">×</button>
        </button>
        <div class="kit-color-picker-drawer__nail-label">Color ${colorNumber}</div>
      `;
      drawerNailList.appendChild(newDrawerNailItem);
      
      // Set up event listeners
      const newButton = newNailItem.querySelector('.kit-color-picker-builder-nail-button');
      const newRemoveButton = newNailItem.querySelector('.kit-color-picker-builder-nail-remove');
      
      if (newButton) {
        newButton.addEventListener('click', (e) => {
          if (!e.target.classList.contains('kit-color-picker-builder-nail-remove')) {
            this.currentColorIndex = `extra-${slotIndex}`;
            this.openDrawer(`extra-${slotIndex}`);
          }
        });
      }
      
      if (newRemoveButton) {
        newRemoveButton.addEventListener('click', (e) => {
          e.stopPropagation();
          this.removeExtraColor(`extra-${slotIndex}`);
        });
      }
      
      // If color data exists, apply the color styling
      if (colorData && hasColor) {
        // Add to extra colors map
        this.extraColors.set(`extra-${slotIndex}`, colorData);
        
        // Set the color display
        const colorDisplay = newNailItem.querySelector('.kit-color-picker-builder-nail-button-color');
        const drawerColorDisplay = newDrawerNailItem.querySelector('.kit-color-picker-drawer__nail-color');
        
        if (colorDisplay && colorData.imageUrl) {
          colorDisplay.style.backgroundImage = `url("${colorData.imageUrl}")`;
          colorDisplay.style.backgroundSize = 'cover';
          colorDisplay.style.backgroundPosition = 'center';
          colorDisplay.style.backgroundRepeat = 'no-repeat';
          
          if (drawerColorDisplay) {
            drawerColorDisplay.style.backgroundImage = `url("${colorData.imageUrl}")`;
            drawerColorDisplay.style.backgroundSize = 'cover';
            drawerColorDisplay.style.backgroundPosition = 'center';
            drawerColorDisplay.style.backgroundRepeat = 'no-repeat';
          }
        } else if (colorDisplay && colorData.color) {
          colorDisplay.style.backgroundColor = colorData.color;
          if (drawerColorDisplay) {
            drawerColorDisplay.style.backgroundColor = colorData.color;
          }
        }
      }
      
      const drawerButton = newDrawerNailItem.querySelector('.kit-color-picker-drawer__nail-button');
      const drawerRemoveButton = newDrawerNailItem.querySelector('.kit-color-picker-drawer__nail-remove');
      
      if (drawerButton) {
        drawerButton.addEventListener('click', (e) => {
          if (!e.target.classList.contains('kit-color-picker-drawer__nail-remove')) {
            this.currentColorIndex = `extra-${slotIndex}`;
            this.highlightCurrentSlot();
          }
        });
      }
      
      if (drawerRemoveButton) {
        drawerRemoveButton.addEventListener('click', (e) => {
          e.stopPropagation();
          this.removeExtraColor(`extra-${slotIndex}`);
        });
      }
      
      return { nailItem: newNailItem, drawerNailItem: newDrawerNailItem };
    }
  
    advanceToNextEmptySlot() {
      if (this.selectedColors.size < this.selectableColors) {
        for (let i = 1; i <= this.selectableColors; i++) {
          if (!this.selectedColors.has(i)) {
            this.currentColorIndex = i;
            this.highlightCurrentSlot();
            break;
          }
        }
      } else if (this.allowAdditionalColors) {
        // All regular slots filled, advance to first extra slot if additional colors are allowed
        this.currentColorIndex = 'extra-0';
        this.highlightCurrentSlot();
        
        // If we're in the drawer context, keep the drawer open and highlight the extra color slot
        if (this.drawer && this.drawer.classList.contains('is-active')) {
          this.openDrawer('extra-0');
        }
      }
    }
  
    removeColor(colorIndex) {
      const index = parseInt(colorIndex);
      this.selectedColors.delete(index);
  
      const nailItem = this.querySelector(`.nail-color-${index}`);
      if (!nailItem) return;
  
      const nailButton = nailItem.querySelector('.kit-color-picker-builder-nail-button');
      const removeButton = nailItem.querySelector('.kit-color-picker-builder-nail-remove');
      const colorDisplay = nailButton.querySelector('.kit-color-picker-builder-nail-button-color');
      if (!nailButton || !removeButton || !colorDisplay) return;
  
      // Remove selected class
      nailButton.classList.remove('selected');
      nailItem.classList.remove('has-color');
      removeButton.classList.add('hidden');
      colorDisplay.style.backgroundImage = '';
      colorDisplay.style.backgroundColor = '';
  
      // Set current slot to the removed one
      this.currentColorIndex = index;
  
      // Update drawer
      this.updateDrawerColorSelector();
  
      // Update button text
      this.updateButtonText();
      this.updateDrawerButtonText();
    }
  
    resetAllColors() {
      this.selectedColors.clear();
      this.extraColors.clear();  // Clear the extra colors map
  
      // Reset regular color slots
      for (let i = 1; i <= this.selectableColors; i++) {
        const nailItem = this.querySelector(`.nail-color-${i}`);
        if (nailItem) {
          const nailButton = nailItem.querySelector('.kit-color-picker-builder-nail-button');
          const removeButton = nailItem.querySelector('.kit-color-picker-builder-nail-remove');
          const colorDisplay = nailButton.querySelector('.kit-color-picker-builder-nail-button-color');
  
          if (nailButton && removeButton && colorDisplay) {
            nailButton.classList.remove('selected');
            nailItem.classList.remove('has-color');
            removeButton.classList.add('hidden');
            colorDisplay.style.backgroundImage = '';
            colorDisplay.style.backgroundColor = '';
          }
        }
      }
  
      // Reset the initial extra color slot
      const extraColorItem = this.querySelector('.nail-color-extra-colors');
      if (extraColorItem) {
        const extraNailButton = extraColorItem.querySelector('.kit-color-picker-builder-nail-button');
        const extraRemoveButton = extraColorItem.querySelector('.kit-color-picker-builder-nail-remove');
        const extraColorDisplay = extraNailButton.querySelector('.kit-color-picker-builder-nail-button-color');
        
        if (extraNailButton && extraRemoveButton && extraColorDisplay) {
          extraNailButton.classList.remove('selected');
          extraColorItem.classList.remove('has-color');
          extraRemoveButton.classList.add('hidden');
          extraColorDisplay.style.backgroundImage = '';
          extraColorDisplay.style.backgroundColor = '';
        }
      }
  
      // Remove any additional extra color slots
      for (let i = 1; i <= this.extraColorCount; i++) {
        const extraSelector = `.nail-color-extra-colors-${i}`;
        const extraItem = this.querySelector(extraSelector);
        if (extraItem) {
          extraItem.remove();
        }
        
        const drawerExtraSelector = `.drawer-nail-color-extra-colors-${i}`;
        const drawerExtraItem = this.drawer.querySelector(drawerExtraSelector);
        if (drawerExtraItem) {
          drawerExtraItem.remove();
        }
      }
      
      // Reset the extra color counter
      this.extraColorCount = 0;
  
      // Set current color index to the first slot
      this.currentColorIndex = 1;
  
      // Update drawer
      this.updateDrawerColorSelector();
  
      // Update button text and kit-starter-number
      this.updateButtonText();
      this.updateDrawerButtonText();
      
      // Reset the kit-starter-number to 0
      const starterNumbers = document.querySelectorAll('.kit-starter-number');
      starterNumbers.forEach(element => {
        element.textContent = '0';
      });
  
      // Reset progress bar for popup style
      if (this.isPopupStyle) {
        const progressFill = this.drawer?.querySelector('.kit-color-picker-drawer__progress-fill');
        if (progressFill) {
          progressFill.style.width = '0%';
        }
      }
    }
  
    updateButtonText() {
      const addToCartButton = document.querySelector('.product-form__submit');
      if (!addToCartButton) return;
  
      // Make sure the button has the kit class
      addToCartButton.classList.add('kit-color-picker-builder-button');
  
      const buttonText = addToCartButton.querySelector('.kit-color-picker-builder-button-color-value');
      if (!buttonText) return;
  
      // Make sure we're using the latest value from the attribute
      this.selectableColors = parseInt(this.getAttribute('data-number-color')) || 6;
  
      const selectedCount = this.selectedColors.size;
      const remaining = this.selectableColors - selectedCount;
  
      // Update the kit-starter-number elements (both main and drawer)
      const starterNumbers = document.querySelectorAll('.kit-starter-number');
      starterNumbers.forEach(element => {
        element.textContent = Math.min(selectedCount, this.selectableColors);
      });
  
      // Update progress bar for popup style
      if (this.isPopupStyle) {
        this.updateProgressBar();
      }
  
      if (remaining > 0) {
        buttonText.innerHTML = `Choose (${remaining}) <span class="kit-color-picker-builder-button-text-inner">more colors</span>`;
        // Don't disable the button, just indicate visually that more colors are needed
        addToCartButton.classList.add('needs-more-colors');
      } else {
        buttonText.textContent = 'Add to cart';
        addToCartButton.classList.remove('needs-more-colors');
      }
    }
  
    updateProgressBar() {
      if (!this.isPopupStyle) return;
      
      const progressFill = this.drawer?.querySelector('.kit-color-picker-drawer__progress-fill');
      if (!progressFill) return;
  
      // Only count regular colors (not extra colors) for progress
      const selectedCount = Math.min(this.selectedColors.size, this.selectableColors);
      const maxCount = Math.max(this.selectableColors, 1); // Prevent division by zero
      const progressPercent = (selectedCount / maxCount) * 100;
      
      console.log('Progress update:', { 
        selectedCount, 
        totalSelected: this.selectedColors.size, 
        maxCount, 
        progressPercent 
      });
      
      // Force visibility for debugging
      progressFill.style.display = 'block';
      progressFill.style.width = `${progressPercent}%`;
      
      // Set black background for progress fill
      progressFill.style.backgroundColor = '#000';
      progressFill.style.width = `${progressPercent}%`;
    }
  
    updateDrawerButtonText() {
      const applyButton = this.drawer?.querySelector('.kit-color-picker-drawer__apply');
      if (!applyButton) return;
  
      const selectedCount = this.selectedColors.size;
      const totalColors = this.selectableColors;
      
      if (selectedCount === 0) {
        applyButton.textContent = `Choose ${totalColors} colors`;
      } else if (selectedCount < totalColors) {
        const remaining = totalColors - selectedCount;
        applyButton.textContent = `Choose ${remaining} more color${remaining !== 1 ? 's' : ''}`;
      } else {
        applyButton.textContent = 'Add to bag';
      }
      
      // Always keep button enabled
      applyButton.disabled = false;
    }
  
    setupAddToCartListener() {
      // Find the product form and the button
      const productForm = document.querySelector('product-form');
      const form = productForm?.querySelector('form');
      const addToCartButton = document.querySelector('.product-form__submit');
  
      if (!form || !addToCartButton) {
        console.warn('KitColorPickerBuilder: Could not find form or add to cart button.');
        return;
      }
  
      // Ensure the button has our identifier class
      addToCartButton.classList.add('kit-color-picker-builder-button');
  
      addToCartButton.addEventListener('click', (event) => {
        // Check if all colors are selected
        const allColorsSelected = this.selectedColors.size === this.selectableColors;
  
        // Always prevent the default form submission
        event.preventDefault();
        event.stopPropagation();
  
        if (!allColorsSelected) {
  
          let emptySlotIndex = null;
          for (let i = 1; i <= this.selectableColors; i++) {
            if (!this.selectedColors.has(i)) {
              emptySlotIndex = i;
              break;
            }
          }
  
          if (emptySlotIndex !== null) {
            // Open the drawer for the first empty slot
            this.currentColorIndex = emptySlotIndex;
            this.openDrawer(emptySlotIndex);
          } else {
            // As a fallback, open the drawer for slot 1
            this.currentColorIndex = 1;
            this.openDrawer(1);
          }
          return;
        }
  
        if (this.isUpsellEnabled) {
          this.showUpsellPopup();
        } else {
          this.addKitToCart();
        }
      }, true);
    }
  
    addKitToCart(upsellItems = []) { // Accept optional upsell items
      // Disable the button and show loading state
      const addToCartButton = document.querySelector('.product-form__submit.kit-color-picker-builder-button');
      // Also handle disabling upsell buttons if called from there
      const upsellAddBtn = this.upsellAddButton;
      const upsellDeclineBtn = this.upsellDeclineButton;
  
      if (addToCartButton) {
        addToCartButton.setAttribute('aria-disabled', true);
        addToCartButton.classList.add('loading');
        const spinner = addToCartButton.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.remove('hidden');
      }
      if (upsellAddBtn) upsellAddBtn.disabled = true;
      if (upsellDeclineBtn) upsellDeclineBtn.disabled = true;
  
      // Get the current timestamp
      const now = new Date();
      const time = now.getTime();
      const dateTime = now.toISOString();
  
      // Get the main product variant ID
      const mainProductVariantId = document.querySelector('input[name="id"].product-variant-id')?.value;
      if (!mainProductVariantId) {
        console.error('Main product variant ID not found');
        this.handleAddToCartError('Could not find main product variant');
        return;
      }
  
      // Make sure we have the latest config values
      this.config = {
        kitBuilderSelectedKitVariantId: this.getAttribute('data-kit-builder-selected-kit-variant-id'),
        kitBuilderBundleDiscount: this.getAttribute('data-kit-builder-bundle-discount'),
        kitGiftVariantIds: this.getAttribute('data-kit-gift-variant-ids')
      };
  
      let items = [];
  
      this.selectedColors.forEach((colorInfo, index) => {
        items.push(this.kitObject(colorInfo.variantId, time, dateTime));
      });
  
      // Add extra colors as separate items (not as part of the kit)
      // Extract discount value from the element or attribute
      const discountValue = this.extraColorsDiscount || 
                          this.querySelector('.nail-color-extra-colors .kit-color-picker-builder-nail-discount-badge')?.textContent?.replace(/[^0-9]/g, '') || 
                          '0';
      
      this.extraColors.forEach((colorInfo, extraIndex) => {
        items.push({
          id: colorInfo.variantId,
          quantity: 1,
          properties: {
            _extra_color: true,
            _extra_color_index: extraIndex,
            _kit_timestamp: time,
            _total_discount: discountValue
          }
        });
      });
  
      // Add free gift products if available
      if (this.config.kitGiftVariantIds) {
        const giftVariantIds = this.config.kitGiftVariantIds.split(',');
        giftVariantIds.forEach(giftVariantId => {
          if (giftVariantId && giftVariantId.trim() !== '') {
            items.push(this.variantGiftObject(giftVariantId.trim(), time, dateTime));
          }
        });
      }
  
      // Add the main product with kit properties
      items.push({
        id: mainProductVariantId,
        quantity: 1,
        properties: {
          _kit_timestamp: time,
          _kit_date_and_time: dateTime,
          _kit_variant: true,
          _bundle_parent_id: `gid://shopify/ProductVariant/${this.config.kitBuilderSelectedKitVariantId}`,
          _bundle_parent_discount: this.config.kitBuilderBundleDiscount,
        }
      });
  
      // Add selected upsell items
      if (upsellItems && upsellItems.length > 0) {
        items = items.concat(upsellItems); // Add upsell items to the array
      }
  
      // Send the cart add request
      fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: 'cart-drawer,cart-icon-bubble' // Ensure these sections are updated
        })
      })
        .then(response => response.json())
        .then(data => {
          if (data.status && (data.status < 200 || data.status >= 300)) { // Check for non-success status codes
            throw new Error(data.description || data.message || 'Error adding to cart');
          }

          // Close upsell popup if open
          if (this.upsellPopup) {
            this.hideUpsellPopup();
          }

          // Fetch and update cart drawer sections
          const fetchCartSections = async () => {
            try {
              const cartDrawerResponse = await fetch(window.Shopify.routes.root + 'cart?section_id=cart-drawer');
              const cartDrawerHtml = await cartDrawerResponse.text();
              
              const cartIconResponse = await fetch(window.Shopify.routes.root + 'cart?section_id=cart-icon-bubble');
              const cartIconHtml = await cartIconResponse.text();
              
              const parser = new DOMParser();
              const cartDrawerDoc = parser.parseFromString(cartDrawerHtml, 'text/html');
              const newCartDrawer = cartDrawerDoc.querySelector('#CartDrawer');
              
              if (newCartDrawer) {
                const currentCartDrawer = document.querySelector('#CartDrawer');
                if (currentCartDrawer) {
                  currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
                }
              }
              
              const cartIconDoc = parser.parseFromString(cartIconHtml, 'text/html');
              const newCartIcon = cartIconDoc.querySelector('.shopify-section');
              
              if (newCartIcon) {
                const currentCartIcon = document.querySelector('#cart-icon-bubble');
                if (currentCartIcon) {
                  currentCartIcon.innerHTML = newCartIcon.innerHTML;
                }
              }
              
              const cartDrawer = document.querySelector('cart-drawer');
              if (cartDrawer) {
                cartDrawer.classList.remove('is-empty');
                const drawerInner = cartDrawer.querySelector('.drawer__inner');
                if (drawerInner) {
                  drawerInner.classList.remove('is-empty');
                }
                
                setTimeout(() => {
                  cartDrawer.classList.add('animate', 'active');
                  document.body.classList.add('overflow-hidden');
                }, 100);
              }
            } catch (error) {
              console.error('Error fetching cart sections:', error);
            } finally {
              // Re-enable buttons
              if (addToCartButton) {
                addToCartButton.classList.remove('loading');
                addToCartButton.removeAttribute('aria-disabled');
                const spinner = addToCartButton.querySelector('.loading-overlay__spinner');
                if (spinner) spinner.classList.add('hidden');
              }
              if (upsellAddBtn) upsellAddBtn.disabled = false;
              if (upsellDeclineBtn) upsellDeclineBtn.disabled = false;
            }
          };
          
          fetchCartSections();
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          this.handleAddToCartError(error.message);
        });
    }
  
    kitObject(variantId, time, dateTime, quantity = 1) {
      return {
        id: variantId,
        quantity: quantity,
        properties: {
          _kit_timestamp: time,
          _kit_date_and_time: dateTime,
          _kit_variant: true,
          _bundle_parent_id: `gid://shopify/ProductVariant/${this.config.kitBuilderSelectedKitVariantId}`,
          _bundle_parent_discount: this.config.kitBuilderBundleDiscount,
        },
      };
    }
  
    variantGiftObject(variantId, time, dateTime) {
      return {
        id: variantId,
        quantity: 1,
        properties: {
          _kit_timestamp: time,
          _kit_date_and_time: dateTime,
          _kit_builder_free_gift: 'Yes',
        },
      };
    }
  
    // We no longer need the handleAddToCartSuccess method since we're redirecting directly to checkout
  
    handleAddToCartError(errorMessage) {
      const addToCartButton = document.querySelector('.product-form__submit.kit-color-picker-builder-button');
      const upsellAddBtn = this.upsellAddButton;
      const upsellDeclineBtn = this.upsellDeclineButton;
  
      // Re-enable main ATC button
      if (addToCartButton) {
        addToCartButton.classList.remove('loading');
        addToCartButton.removeAttribute('aria-disabled');
        const spinner = addToCartButton.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
  
      // Re-enable upsell buttons
      if (upsellAddBtn) upsellAddBtn.disabled = false;
      if (upsellDeclineBtn) upsellDeclineBtn.disabled = false;
  
      // Show error message (consider showing it near the button clicked, or in a general modal/area)
      // For simplicity, using the existing product form error area
      const errorContainer = document.querySelector('.product-form__error-message-wrapper');
      const errorMessageElement = document.querySelector('.product-form__error-message');
  
      if (errorContainer && errorMessageElement) {
        errorContainer.removeAttribute('hidden');
        errorMessageElement.textContent = errorMessage || 'Error adding item(s) to cart. Please try again.';
  
        // Hide error after 5 seconds
        setTimeout(() => {
          errorContainer.setAttribute('hidden', '');
        }, 5000);
      } else {
        // Fallback to alert if error container not found
        alert(errorMessage || 'Error adding item(s) to cart. Please try again.');
      }
    }
  
    setupImageSwitcher() {
      const showFirstImg = this.drawer.querySelector('#show_first_img');
      const showSecondImg = this.drawer.querySelector('#show_second_img');
  
      if (showFirstImg && showSecondImg) {
        showFirstImg.addEventListener('click', () => {
          showFirstImg.classList.add('select-image__option-bundle--active');
          showSecondImg.classList.remove('select-image__option-bundle--active');
          this.switchColorImages(false);
        });
  
        showSecondImg.addEventListener('click', () => {
          showSecondImg.classList.add('select-image__option-bundle--active');
          showFirstImg.classList.remove('select-image__option-bundle--active');
          this.switchColorImages(true);
        });
      }
    }
  
    switchColorImages(useSecondImage) {
      if (!this.drawer) return;
  
      const colorOptions = this.drawer.querySelectorAll('.color-option');
      colorOptions.forEach(option => {
        const imageContainer = option.querySelector('.color-option__image');
        const img = imageContainer?.querySelector('img');
  
        if (imageContainer && img) {
          // Determine which image URL to use
          const firstImage = imageContainer.dataset.firstImage;
          const secondImage = imageContainer.dataset.secoundImage;
  
          if (firstImage && secondImage && secondImage !== 'null') {
            img.classList.add('switching');
  
            if (useSecondImage) {
              img.src = secondImage;
              img.removeAttribute('srcset');
            } else {
              img.src = firstImage;
            }
  
            setTimeout(() => {
              img.classList.remove('switching');
            }, 300);
          }
        }
      });
    }
  
    setupSorting() {
      const popoutToggle = this.drawer.querySelector('.popout__toggle');
      const sortList = this.drawer.querySelector('#sort-list');
  
      if (popoutToggle && sortList) {
        // Toggle dropdown
        popoutToggle.addEventListener('click', () => {
          const isExpanded = popoutToggle.getAttribute('aria-expanded') === 'true';
  
          popoutToggle.setAttribute('aria-expanded', !isExpanded);
  
          if (!isExpanded) {
            sortList.style.display = 'block';
            sortList.setAttribute('aria-expanded', 'true');
          } else {
            sortList.style.display = 'none';
            sortList.setAttribute('aria-expanded', 'false');
          }
        });
  
        // Handle sort option selection
        const sortOptions = sortList.querySelectorAll('.popout-list__option');
        sortOptions.forEach(option => {
          option.addEventListener('click', (e) => {
            e.preventDefault();
  
            const sortBy = option.getAttribute('data-value') || 'manual';
            const sortText = option.querySelector('span').textContent.trim();
  
            const sortButtonText = popoutToggle.querySelector('[data-sort-button-text]');
            if (sortButtonText) {
              sortButtonText.textContent = sortText;
            }
  
            const popoutElement = this.drawer.querySelector('.popout');
            if (popoutElement) {
              popoutElement.setAttribute('data-sort-enabled', sortBy);
            }
  
            sortList.style.display = 'none';
            sortList.setAttribute('aria-expanded', 'false');
            popoutToggle.setAttribute('aria-expanded', 'false');
  
            const listItem = option.closest('li.popout-list__item');
            let collectionHandle = listItem ? listItem.getAttribute('data-collection-handle') : null;
  
            if (!collectionHandle) {
              const metafield = this.productHandle || document.querySelector('meta[name="kit-color-collection"]')?.content;
              collectionHandle = metafield || 'color-choose-products';
            }
  
            this.fetchSortedProducts(collectionHandle, sortBy);
          });
        });
      }
    }
  
    fetchSortedProducts(collectionHandle, sortBy) {
      const grid = this.drawer.querySelector('.kit-color-picker-drawer__grid');
      if (grid) {
        grid.classList.add('loading');
  
        if (typeof cleanupOkendoReviews === 'function') {
          cleanupOkendoReviews();
        }
  
        fetch(`/collections/${collectionHandle}?sort_by=${sortBy}&view=kit-color-filter`, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
          .then(response => response.text())
          .then(html => {
  
            const tempContainer = document.createElement('div');
            tempContainer.innerHTML = html;
  
            const newGrid = tempContainer.querySelector('.kit-color-picker-drawer__grid') ||
              tempContainer.querySelector('.color-picker-slider');
  
            if (newGrid) {
              grid.innerHTML = newGrid.innerHTML;
  
              this.drawer.querySelectorAll('.color-option').forEach(option => {
                option.addEventListener('click', () => {
                  try {
                    const variantId = option.dataset.variantId;
                    const colorInfo = JSON.parse(option.dataset.colorInfo);
                    const colorImage = option.querySelector('.color-option__image')?.dataset.firstImage;
                    this.selectColor(variantId, colorInfo, option, colorImage);
                  } catch (error) {
                    console.error('Error selecting color:', error);
                  }
                });
              });
  
              const showSecondImg = this.drawer.querySelector('#show_second_img');
              const useSecondImage = showSecondImg && showSecondImg.classList.contains('select-image__option-bundle--active');
  
              setTimeout(() => {
                this.switchColorImages(useSecondImage);
              }, 100);
              
              if (window.okeWidgetApi && typeof window.okeWidgetApi.initAllWidgets === 'function') {
                window.okeWidgetApi.initAllWidgets();
              }
            }
  
            grid.classList.remove('loading');
          })
          .catch(error => {
            console.error('Error fetching sorted products:', error);
            grid.classList.remove('loading');
          });
      }
    }
  
    setupExtraColorsEventListeners() {
      console.log('Setting up extra colors event listeners');
      
      // Set initial extra color index to 0
      const firstExtraColorIndex = 'extra-0';
      
      // Event listener for the initial extra color nail
      const extraColorNail = this.querySelector('.nail-color-extra-colors');
      if (extraColorNail) {
        // Set data attribute on the nail item for consistency
        extraColorNail.dataset.extraIndex = '0';
        
        const extraColorButton = extraColorNail.querySelector('.kit-color-picker-builder-nail-button');
        if (extraColorButton) {
          extraColorButton.dataset.colorIndex = firstExtraColorIndex;
          
          extraColorButton.addEventListener('click', (e) => {
            // Only allow adding extra colors if all regular colors are selected
            if (this.selectedColors.size >= this.selectableColors) {
              if (!e.target.classList.contains('kit-color-picker-builder-nail-remove')) {
                console.log('Opening drawer for extra color selection');
                // Set current color index to the extra color and open drawer
                this.currentColorIndex = firstExtraColorIndex;
                this.openDrawer(firstExtraColorIndex);
              }
            } else {
              // Show message that user needs to fill all regular colors first
              alert('Please select all regular colors before adding additional colors.');
            }
          });
        }
        
        // Make sure remove button has proper event handler
        const removeButton = extraColorNail.querySelector('.kit-color-picker-builder-nail-remove');
        if (removeButton) {
          removeButton.addEventListener('click', (e) => {
            e.stopPropagation();
            this.removeExtraColor(firstExtraColorIndex);
          });
        }
      }
      
      // Do the same for the drawer item
      const drawerExtraColorNail = this.drawer.querySelector('.drawer-nail-color-extra-colors');
      if (drawerExtraColorNail) {
        drawerExtraColorNail.dataset.extraIndex = '0';
        
        const drawerButton = drawerExtraColorNail.querySelector('.kit-color-picker-drawer__nail-button');
        if (drawerButton) {
          drawerButton.dataset.colorIndex = firstExtraColorIndex;
        }
      }
    }
  
    // Helper method to check if a string is in the format 'extra-X'
    isExtraColorIndex(index) {
      return typeof index === 'string' && index.startsWith('extra-');
    }
  }
  
  customElements.define('kit-color-picker-builder', KitColorPickerBuilder);
  